import { logout } from '../auth/api.js';
import { auth } from '../auth/state.js';
import type { Router } from '../router.js';

/**
 * Top navigation bar — only visible when logged in. Subscribes to auth state
 * so the username and links re-render after login/logout/profile changes.
 */
export function mountNavbar(host: HTMLElement, router: Router): void {
  const render = (): void => {
    host.innerHTML = '';
    if (!auth.user) {
      host.style.display = 'none';
      return;
    }
    host.style.display = '';

    const left = document.createElement('div');
    left.className = 'nav-left';
    // Image logo instead of the text "War of Lyanx" — keeps the navbar
    // tight and instantly recognizable. Clicking the logo returns home.
    const brand = document.createElement('a');
    brand.className = 'nav-brand';
    brand.href = '/';
    const logo = document.createElement('img');
    logo.src = '/sprites/logowol.png';
    logo.alt = 'War of Lyanx';
    logo.className = 'nav-brand-logo';
    brand.appendChild(logo);
    left.appendChild(brand);

    const links = document.createElement('div');
    links.className = 'nav-links';
    // Cat-themed navigation labels — same routes, friendlier signs.
    links.appendChild(navLink('Le jardin',          '/'));
    links.appendChild(navLink('Mon coussin',        '/profile'));
    links.appendChild(navLink('Tableau des moustaches', '/leaderboard'));
    // Admin-only nav link — visible only when the server flagged the
    // current user with `is_admin = true`. The route still gates
    // server-side regardless of UI visibility (defense in depth).
    if (auth.user.isAdmin) {
      links.appendChild(navLink('⚙ Administration', '/admin'));
    }

    const right = document.createElement('div');
    right.className = 'nav-right';
    const userTag = document.createElement('span');
    userTag.className = 'nav-user';
    userTag.textContent = auth.user.username;
    const logoutBtn = document.createElement('button');
    logoutBtn.type = 'button';
    logoutBtn.className = 'nav-logout';
    logoutBtn.textContent = 'Déconnexion';
    logoutBtn.addEventListener('click', async () => {
      logoutBtn.disabled = true;
      try {
        await logout();
      } catch {
        // ignore — clear local state regardless
      } finally {
        auth.user = null;
        router.navigate('/login');
      }
    });
    right.append(userTag, logoutBtn);

    host.append(left, links, right);
  };

  auth.subscribe(render);
  render();
}

function navLink(label: string, path: string): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'nav-link';
  a.href = path;
  a.textContent = label;
  return a;
}
